import React, { useEffect, useState } from 'react';
import imgLogo from '../img/viti-crm-w.png';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import VadoLogoLogin from '../main/vadologologin';
import backgroundImg from '../img/building.jpg';
import Alert from '@mui/material/Alert';
import cred from '../devcred';
import packageInfo from '../../package.json';
import { Grid, Paper, Select, MenuItem } from '@mui/material';
import SpinnerButton from '../common/spinnerbutton';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import languages from '../i18n/languages';

const headerStyle = {
    backgroundImage: `url(${backgroundImg})`,
    height: '100vh',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
};

const containerStyle = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    overflowY: 'scroll',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
};

function getCanvasFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = "16px 'Arial'";
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Canvas Fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Canvas Fingerprint', 4, 17);
    return canvas.toDataURL();
}

async function getFingerprint() {
    const canvasFingerprint = getCanvasFingerprint();
    const fingerprint = {
        // Existing properties
        cookiesEnabled: navigator.cookieEnabled,
        localStorageEnabled: !!window.localStorage,
        sessionStorageEnabled: !!window.sessionStorage,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        browserPlugins: Array.from(navigator.plugins).map((p) => p.name),
        cpuCount: navigator.hardwareConcurrency,
        memoryGB: navigator.deviceMemory || 'unavailable',
        canvasFingerprint: canvasFingerprint,

        // Additional fingerprinting properties
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        colorDepth: window.screen.colorDepth,
        pixelRatio: window.devicePixelRatio,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset(),
        languages: navigator.languages,
        doNotTrack: navigator.doNotTrack,
        touchSupport: 'ontouchstart' in window,
        maxTouchPoints: navigator.maxTouchPoints || 0,
        audioContext: (() => {
            try {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                const context = new AudioContext();
                const result = context.sampleRate;
                context.close();
                return result;
            } catch (e) {
                return 'unavailable';
            }
        })(),
        webGLVendor: (() => {
            try {
                const canvas = document.createElement('canvas');
                const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
                const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
                return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
            } catch (e) {
                return 'unavailable';
            }
        })(),
        webGLRenderer: (() => {
            try {
                const canvas = document.createElement('canvas');
                const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
                const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
                return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
            } catch (e) {
                return 'unavailable';
            }
        })(),
        installedFonts: (() => {
            const fonts = [
                'Arial',
                'Verdana',
                'Times New Roman',
                'Courier New',
                'Georgia',
                'Palatino',
                'Garamond',
                'Comic Sans MS',
                'Trebuchet MS',
                'Impact',
            ];
            const available = [];
            const testString = 'mmmmmmmmmmlli';
            const testSize = '72px';
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            context.font = testSize + ' monospace';
            const baseWidth = context.measureText(testString).width;

            fonts.forEach((font) => {
                context.font = testSize + ' ' + font + ', monospace';
                const width = context.measureText(testString).width;
                if (width !== baseWidth) {
                    available.push(font);
                }
            });
            return available;
        })(),
        hasIndexedDB: !!window.indexedDB,
        hasWebSQL: !!window.openDatabase,
        hasWebWorkers: !!window.Worker,
        hasServiceWorker: 'serviceWorker' in navigator,
        hasPushManager: 'PushManager' in window,
        hasNotifications: 'Notification' in window,
        hasGeolocation: 'geolocation' in navigator,
        hasWebRTC: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
        hasWebAssembly: typeof WebAssembly === 'object',
        hasSharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
        hasWebGL: (() => {
            try {
                const canvas = document.createElement('canvas');
                return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
            } catch (e) {
                return false;
            }
        })(),
        hasWebGL2: (() => {
            try {
                const canvas = document.createElement('canvas');
                return !!canvas.getContext('webgl2');
            } catch (e) {
                return false;
            }
        })(),
    };
    return JSON.stringify(fingerprint);
}

export default function Login(props) {
    const isCustomerSite = process.env.REACT_APP_TARGET === 'customerui';
    const usernameInput = React.createRef();
    const passwordInput = React.createRef();
    const dev = process.env.NODE_ENV === 'development' ? true : false;
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [inputWarning, setInputWarning] = useState('');
    const [changingLang, setChangingLang] = useState(false);
    const { t, i18n } = useTranslation();
    const selectedLang = i18n.language;

    useEffect(() => {
        usernameInput.current.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usernameInput.current]);

    const inputChange = (evt) => {
        const name = evt.target.name;
        const value = evt.target.value;

        switch (name) {
            case 'username':
                setUsername(value);
                break;
            case 'password':
                setPassword(value);
                break;
            default:
        }
    };

    const DoSubmit = async (username, password) => {
        props.onSubmit(username, password, await getFingerprint());
    };

    const onClick = async () => {
        if (username.length < 3 || password.length < 8) {
            setInputWarning(t('login.invalidUsernameOrPassword'));
        } else {
            await DoSubmit(username, password);
            setUsername('');
            setPassword('');
            setInputWarning('');
        }
    };

    const demoLogin = async () => {
        await DoSubmit(cred.username, cred.password);
    };
    const demoLoginUserOnly = async () => {
        await DoSubmit('testimies@yksi.fi', 'Penaali1');
    };
    const demoLoginProd = async () => {
        await DoSubmit(cred.usernameprod, cred.password);
    };
    const demoLoginInvoice = async () => {
        await DoSubmit(cred.usernameinv, cred.password);
    };

    const getSelectedLang = () => {
        return languages.find((l) => l.lang === selectedLang);
    };

    const changeLanguage = (newLang) => {
        setChangingLang(false);
        document.cookie = `lang=${newLang};max-age=31536000;path=/`;
        i18n.changeLanguage(newLang);
    };

    if (props.isAuthenticated) {
        return null;
    }

    return (
        <div style={headerStyle}>
            <div style={containerStyle}>
                <Grid container style={{ marginTop: '50px' }}>
                    <Grid item xs={12} style={{ textAlign: 'center' }}>
                        {isCustomerSite && (
                            <div style={{ paddingBottom: '10px' }}>
                                <VadoLogoLogin />
                            </div>
                        )}
                        {!isCustomerSite && <img src={imgLogo} alt="logo" style={{ paddingBottom: '10px' }} />}
                    </Grid>
                    <Grid item xs={12} style={{ textAlign: 'center', margin: '10px' }}>
                        <Paper
                            style={{
                                margin: 'auto',
                                textAlign: 'center',
                                backgroundColor: 'white',
                                maxWidth: '500px',
                                borderRadius: '10px',
                                padding: '1em',
                            }}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <h2>{t('login.title')}</h2>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        id="username"
                                        name="username"
                                        label={t('login.username')}
                                        value={username}
                                        variant="outlined"
                                        sx={{ mx: 1, width: '95%' }}
                                        margin="normal"
                                        onChange={inputChange}
                                        inputRef={usernameInput}
                                        onKeyPress={(event) => {
                                            if (event.key === 'Enter') {
                                                passwordInput.current.focus();
                                            }
                                        }}
                                        disabled={props.loginInProgress}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        id="password"
                                        name="password"
                                        label={t('login.password')}
                                        value={password}
                                        variant="outlined"
                                        sx={{ mx: 1, width: '95%' }}
                                        type="password"
                                        autoComplete=""
                                        margin="normal"
                                        onChange={inputChange}
                                        inputRef={passwordInput}
                                        onKeyPress={(event) => {
                                            if (event.key === 'Enter') {
                                                onClick();
                                            }
                                        }}
                                        disabled={props.loginInProgress}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <br />
                                    {props.loginRateLimited && (
                                        <Alert severity="error">{t('login.loginRateLimited')}</Alert>
                                    )}
                                    {props.loginFailed && <Alert severity="warning">{t('login.loginFailed')}</Alert>}
                                    {inputWarning.length > 0 && <Alert severity="warning">{inputWarning}</Alert>}
                                </Grid>
                                <Grid item xs={12} style={{ marginBottom: '10px' }}>
                                    <SpinnerButton
                                        inProgress={props.loginInProgress}
                                        text={t('login.title')}
                                        fullWidth={true}
                                        onClick={onClick}
                                    />
                                </Grid>
                                {dev && (
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            onClick={demoLogin}
                                            sx={{
                                                m: 1,
                                                width: '95%',
                                                bgcolor: 'grey.300',
                                                color: 'text.primary',
                                                '&:hover': { bgcolor: 'grey.400' },
                                            }}>
                                            demo login
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={demoLoginUserOnly}
                                            sx={{
                                                m: 1,
                                                width: '95%',
                                                bgcolor: 'grey.300',
                                                color: 'text.primary',
                                                '&:hover': { bgcolor: 'grey.400' },
                                            }}>
                                            demo login User-only
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={demoLoginProd}
                                            sx={{
                                                m: 1,
                                                width: '95%',
                                                bgcolor: 'grey.300',
                                                color: 'text.primary',
                                                '&:hover': { bgcolor: 'grey.400' },
                                            }}>
                                            demo-production
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={demoLoginInvoice}
                                            sx={{
                                                m: 1,
                                                width: '95%',
                                                bgcolor: 'grey.300',
                                                color: 'text.primary',
                                                '&:hover': { bgcolor: 'grey.400' },
                                            }}>
                                            demo-invoicing
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                        <br />
                        <br />
                        {!changingLang && (
                            <Grid item xs={12}>
                                <Button
                                    onClick={() => {
                                        setChangingLang(true);
                                    }}>
                                    {getSelectedLang().flagImg}
                                    <span style={{ color: 'white', marginLeft: '5px' }}>
                                        {t(getSelectedLang().name)}
                                    </span>
                                </Button>
                            </Grid>
                        )}
                        {changingLang && (
                            <Select
                                required
                                error={false}
                                id="language"
                                name="language"
                                value={selectedLang}
                                open={true}
                                onChange={(evt) => changeLanguage(evt.target.value)}>
                                {languages.map((lang) => (
                                    <MenuItem key={lang.lang} value={lang.lang}>
                                        {lang.flagImg}
                                        <span style={{ marginLeft: '5px' }}>{t(lang.name)}</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                        <br />
                        {isCustomerSite && (
                            <div style={{ color: 'white' }}>
                                VADO myyntitilaukset <br />
                                Powered by MyynninTuki&trade; &copy;&nbsp;2019-2026 Vitilumi Oy, v{packageInfo.version}
                            </div>
                        )}
                        {!isCustomerSite && (
                            <div style={{ color: 'white' }}>
                                MyynninTuki&trade; &copy;&nbsp;2019-2026 Vitilumi Oy, v{packageInfo.version}
                            </div>
                        )}
                        <br />
                        <br />
                    </Grid>
                </Grid>
            </div>
        </div>
    );
}

Login.propTypes = {
    show: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    loginFailed: PropTypes.bool,
    loginRateLimited: PropTypes.bool,
    loginInProgress: PropTypes.bool,
};
